import { Link } from 'react-router-dom';
import { useAuth } from '../features/auth/AuthContext';
import { usePageTitle } from '../lib/usePageTitle';

/**
 * Homepage pubblica: landing scura in tema mascotte, pensata per convertire.
 * L'accessibilità è integrata (landmark, contrasti, semantica) senza essere
 * un argomento di vendita.
 */

/** Strisce diagonali verdi in tema con la mascotte (decorative). */
const stripeBg: React.CSSProperties = {
  backgroundColor: '#0d1a10',
  backgroundImage:
    'repeating-linear-gradient(115deg, transparent 0 180px, rgba(57,192,75,0.16) 180px 210px, transparent 210px 320px, rgba(126,211,33,0.10) 320px 338px, transparent 338px 520px)',
};

const RULES: Array<{ icon: string; title: string; text: string }> = [
  {
    icon: '⚽',
    title: '4 partite, zero scuse',
    text: 'Scegli tu le partite. Secco o doppia — ma la doppia dimezza i punti. Hai il coraggio del secco?',
  },
  {
    icon: '🎯',
    title: 'Il risultato esatto',
    text: 'La quinta partita è per i veggenti: azzecca il punteggio preciso e porti a casa il bottino.',
  },
  {
    icon: '🔥',
    title: 'L’OVER premia i folli',
    text: 'Punta sulla partita con più gol. 4 reti ti pagano, 8 ti incoronano. Lo 0-0 ti presenta il conto.',
  },
  {
    icon: '💎',
    title: 'L’originalità paga',
    text: 'Unico a indovinare? Bonus. In gregge col favorito insieme ad altri tre? Malus. Pensa diverso.',
  },
  {
    icon: '🚨',
    title: 'La colonna rossa',
    text: 'Ti dimentichi la schedina? Rigiochi quella vecchia con penalità. La vergogna è compresa nel prezzo.',
  },
  {
    icon: '🐼',
    title: 'Cita, il bot',
    text: 'Un giocatore fantasma che pronostica a caso. Se in classifica sei sotto di lui, ne risponderai al gruppo.',
  },
];

export function HomePage() {
  usePageTitle('');
  const { user } = useAuth();

  const primaryCta = user
    ? { to: '/dashboard', label: 'Vai alla tua dashboard' }
    : { to: '/registrati', label: 'Crea la tua lega — è gratis' };

  return (
    <>
      <a href="#contenuto" className="skip-link">
        Salta al contenuto principale
      </a>

      {/* ---------- HERO scuro ---------- */}
      <div style={stripeBg} className="text-white">
        <header className="px-4 pt-4">
          <nav
            aria-label="Navigazione principale"
            className="mx-auto flex max-w-6xl items-center justify-between"
          >
            <span className="inline-flex items-center gap-2 text-xl font-bold">
              <img src="/favicon.png" alt="" width={36} height={36} className="h-9 w-9" />
              PandaCalcio
            </span>
            {user ? (
              <Link
                to="/dashboard"
                className="rounded-xl border-2 border-white/40 px-4 py-2 font-semibold text-white no-underline hover:border-white"
              >
                La mia dashboard
              </Link>
            ) : (
              <Link
                to="/accedi"
                className="rounded-xl border-2 border-white/40 px-4 py-2 font-semibold text-white no-underline hover:border-white"
              >
                Accedi
              </Link>
            )}
          </nav>
        </header>

        <main id="contenuto">
          <section className="mx-auto flex max-w-6xl flex-col-reverse items-center gap-6 px-4 pb-16 pt-10 sm:flex-row sm:justify-between sm:gap-10 sm:pt-16">
            <div className="max-w-xl text-center sm:text-left">
              <p className="inline-block rounded-full border border-[#7ed321]/60 px-4 py-1 text-sm font-semibold tracking-wide text-[#a5e85c]">
                Serie A 2026/27 · Leghe private tra amici
              </p>
              <h1 className="text-display mt-5">
                Il fantacalcio è finito.
                <br />
                <span className="text-[#7ed321]">Ora si pronostica.</span>
              </h1>
              <p className="mt-5 text-xl text-white/85">
                Crea la tua lega, invita gli amici su WhatsApp e sfidali ogni giornata: schedine,
                bonus, malus e una classifica che non perdona.
              </p>
              <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row">
                <Link
                  to={primaryCta.to}
                  className="button inline-flex items-center justify-center rounded-2xl bg-[#7ed321] px-8 py-4 text-lg font-bold text-[#0d1a10] no-underline shadow-lg shadow-[#7ed321]/25 transition-transform duration-100 hover:bg-[#8fe832] active:scale-[0.98]"
                >
                  {primaryCta.label}
                </Link>
                {!user && (
                  <Link
                    to="/entra-con-codice"
                    className="button inline-flex items-center justify-center rounded-2xl px-6 py-4 text-lg font-semibold text-white underline decoration-white/50 underline-offset-4 hover:decoration-white"
                  >
                    Ho un codice invito
                  </Link>
                )}
              </div>
              <p className="mt-4 text-sm text-white/60">
                Gratis, senza pubblicità. Si installa come app sul telefono.
              </p>
            </div>
            <img
              src="/mascotte.webp"
              alt="La mascotte di PandaCalcio: un panda con la maglia bianconera che stringe un pallone"
              width={340}
              height={340}
              className="h-56 w-56 shrink-0 drop-shadow-[0_12px_35px_rgba(126,211,33,0.35)] sm:h-80 sm:w-80"
            />
          </section>
        </main>
      </div>

      {/* ---------- REGOLE CHE FANNO DISCUTERE ---------- */}
      <section aria-labelledby="regole" className="mx-auto max-w-6xl px-4 py-16">
        <h2 id="regole" className="text-3xl font-bold tracking-tight">
          Regole che accendono il gruppo
        </h2>
        <p className="mt-2 max-w-prose text-lg text-ink-700 dark:text-ink-200">
          Il regolamento PandaCalcio è nato in un bar e si vede. E se non vi piace, ogni lega può
          riscriverselo: punteggi, bonus e cattiverie sono tutti configurabili.
        </p>
        <ul className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {RULES.map((rule) => (
            <li
              key={rule.title}
              className="rounded-2xl border border-ink-200 p-5 transition-transform duration-100 hover:-translate-y-0.5 hover:border-panda-600 dark:border-ink-800 dark:hover:border-panda-400"
            >
              <p aria-hidden="true" className="text-3xl">{rule.icon}</p>
              <h3 className="mt-2 text-lg font-bold">{rule.title}</h3>
              <p className="mt-1 text-ink-700 dark:text-ink-200">{rule.text}</p>
            </li>
          ))}
        </ul>
      </section>

      {/* ---------- COME FUNZIONA ---------- */}
      <section aria-labelledby="come-funziona" className="border-y border-ink-200 bg-ink-50 dark:border-ink-800 dark:bg-ink-900">
        <div className="mx-auto max-w-6xl px-4 py-16">
          <h2 id="come-funziona" className="text-3xl font-bold tracking-tight">
            In campo in due minuti
          </h2>
          <ol className="mt-8 grid gap-6 sm:grid-cols-3">
            {[
              {
                title: 'Crea la lega',
                text: 'Nome, campionato, regole. Cita il bot è opzionale, il divertimento no.',
              },
              {
                title: 'Invita gli amici',
                text: 'Un link su WhatsApp e sono dentro. Fino a 100 partecipanti per lega.',
              },
              {
                title: 'Giocate ogni giornata',
                text: 'Schedine, risultati veri della Serie A, classifica aggiornata e sfottò garantiti.',
              },
            ].map((step, i) => (
              <li key={step.title} className="relative rounded-2xl border border-ink-200 bg-white p-6 dark:border-ink-700 dark:bg-ink-950">
                <p
                  aria-hidden="true"
                  className="absolute -top-4 left-5 flex h-9 w-9 items-center justify-center rounded-full bg-panda-700 text-lg font-bold text-white dark:bg-panda-600"
                >
                  {i + 1}
                </p>
                <h3 className="mt-2 text-lg font-bold">
                  <span className="visually-hidden">Passo {i + 1}: </span>
                  {step.title}
                </h3>
                <p className="mt-1 text-ink-700 dark:text-ink-200">{step.text}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* ---------- APP + CTA FINALE ---------- */}
      <section aria-labelledby="cta-finale" className="mx-auto max-w-6xl px-4 py-16">
        <div style={stripeBg} className="flex flex-col items-center gap-6 rounded-3xl px-6 py-12 text-center text-white sm:flex-row sm:justify-between sm:text-left">
          <div>
            <h2 id="cta-finale" className="text-3xl font-bold tracking-tight">
              La tua lega ti aspetta.
            </h2>
            <p className="mt-2 max-w-md text-lg text-white/85">
              Si installa come una vera app su Android e iPhone, direttamente dal browser.
            </p>
            <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row">
              <Link
                to={user ? '/dashboard' : '/registrati'}
                className="button inline-flex items-center justify-center rounded-2xl bg-[#7ed321] px-8 py-3.5 text-lg font-bold text-[#0d1a10] no-underline hover:bg-[#8fe832] active:scale-[0.98]"
              >
                {user ? 'Vai alla dashboard' : 'Inizia ora'}
              </Link>
              <Link
                to="/installa"
                className="button inline-flex items-center justify-center rounded-2xl px-5 py-3.5 font-semibold text-white underline decoration-white/50 underline-offset-4 hover:decoration-white"
              >
                Come installare l’app
              </Link>
            </div>
          </div>
          <img
            src="/mascotte.webp"
            alt=""
            width={180}
            height={180}
            className="h-40 w-40 shrink-0"
          />
        </div>
      </section>

      <footer className="border-t border-ink-200 px-4 py-6 dark:border-ink-800">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-2 text-sm text-ink-600 dark:text-ink-300">
          <p>PandaCalcio — pronostici calcistici tra amici.</p>
          <nav aria-label="Collegamenti utili" className="flex gap-4">
            <Link to="/installa" className="underline">Installa l’app</Link>
            <Link to="/privacy" className="underline">Privacy</Link>
            <Link to="/cookie" className="underline">Cookie</Link>
          </nav>
        </div>
      </footer>
    </>
  );
}
